import {
  createContext,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { getStats } from "@/api/client";
import type { Rarity, SharkSummary, Stats } from "@/api/types";
import { useAsync, type AsyncState } from "@/hooks/useAsync";
import { usePrefersReducedMotion } from "@/hooks/useMediaQuery";
import { formatCount, yearRange } from "@/lib/format";
import { rarityInfo } from "@/lib/rarity";
import { SpotGlyph } from "@/site/plate";

/** Every route of the site. These URLs are public: certificate links are shared, so keep them. */
export const to = {
  home: "/",
  registry: "/registry",
  about: "/about",
  identify: (id: string) => `/identify/${encodeURIComponent(id)}`,
  recording: (id: string) => `/recording/${encodeURIComponent(id)}`,
  shark: (id: string) => `/shark/${encodeURIComponent(id)}`,
  certificate: (id: string) => `/certificate/${encodeURIComponent(id)}`,
};

// ------------------------------------------------------------------ stats, shared by every page

const StatsContext = createContext<AsyncState<Stats> | null>(null);

export function StatsProvider({ children }: { children: ReactNode }) {
  const stats = useAsync("stats", (signal) => getStats(signal));
  return <StatsContext.Provider value={stats}>{children}</StatsContext.Provider>;
}

/** The registry's stats, or null while loading / on error (callers print a dash). */
export function useStats(): Stats | null {
  const state = useContext(StatsContext);
  return state?.status === "success" ? state.data : null;
}

export function count(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : formatCount(n);
}

// ------------------------------------------------------------------ type

export function SectionHead({
  index,
  title,
  id,
  children,
}: {
  index: string;
  title: string;
  id?: string;
  children?: ReactNode;
}) {
  return (
    <header className="in-grid in-section-head">
      <span className="idx label">{index}</span>
      <h2 id={id}>{title}</h2>
      {children && <div className="aside">{children}</div>}
    </header>
  );
}

/** Rarity as a five-cell gauge and a label; legendary alone takes the signal colour. */
export function RarityMark({ rarity, large = false }: { rarity: Rarity; large?: boolean }) {
  const info = rarityInfo(rarity);
  return (
    <span className={`rm${large ? " rm-lg" : ""}`} data-rarity={rarity}>
      <span className="rm-cells" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <i key={n} data-on={n <= info.stars} />
        ))}
      </span>
      <span className="rm-label">{info.label}</span>
      <span className="sr-only">, {info.stars} of 5</span>
    </span>
  );
}

/** A number that counts up to its value once, like a readout settling. */
export function useSettle(value: number, ms = 900): number {
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (reduced) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min((now - start) / ms, 1);
      setShown(value * (1 - (1 - t) ** 4));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, ms, reduced]);
  return reduced ? value : shown;
}

// ------------------------------------------------------------------ specimen card

/** A specimen in a drawer: `index` is its printed position, `delay` its entrance stagger step. */
export function SpecimenCard({
  shark,
  index = 0,
  delay = index,
}: {
  shark: SharkSummary;
  index?: number;
  delay?: number;
}) {
  const years = yearRange(shark.first_seen, shark.last_seen);
  return (
    <Link
      to={to.shark(shark.id)}
      className="spec anim-in"
      style={{ "--i": delay } as CSSProperties}
    >
      <div className="mono label" style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "var(--ink)" }}>{shark.id}</span>
        <span>{String(index + 1).padStart(2, "0")}</span>
      </div>
      <div className="spec-img">
        {shark.thumb_url ? (
          <img src={shark.thumb_url} alt={`${shark.display_name}, ${shark.id}`} loading="lazy" />
        ) : shark.constellation ? (
          <SpotGlyph
            constellation={shark.constellation}
            label={`${shark.display_name}: hand-marked spots, no photo on file`}
          />
        ) : null}
      </div>
      <p className="spec-name">{shark.display_name}</p>
      <div style={{ display: "grid", gap: 6 }}>
        <RarityMark rarity={shark.rarity} />
        <span className="label">
          ENC {shark.encounters}
          {years ? ` · ${years}` : ""}
        </span>
      </div>
    </Link>
  );
}

// ------------------------------------------------------------------ photo tips

const TIPS: [string, string][] = [
  [
    "Angle",
    "Side-on to the flank. Straight down from above matches other top-down photos, but " +
      "cannot name a new shark; from below or head-first does not work.",
  ],
  ["Framing", "Behind the gills and above the pectoral fin: where the pattern is read."],
  [
    "View",
    "Left flank, right flank and back each carry their own pattern; each matches only its own.",
  ],
  ["Light", "Near the surface, sun behind you, little backscatter."],
  ["Distance", "Close enough that single spots are sharp, with the whole patch in frame."],
];

/** What makes a photograph readable, as a specification table. */
export function PhotoTips() {
  return (
    <ol className="tips">
      {TIPS.map(([k, v]) => (
        <li key={k} style={{ gridTemplateColumns: "7em 1fr" }}>
          <span className="label">{k}</span>
          <span>{v}</span>
        </li>
      ))}
    </ol>
  );
}

// ------------------------------------------------------------------ states

export function Loading({ label }: { label: string }) {
  return (
    <div className="in-wrap in-section" role="status" aria-live="polite">
      <p className="label">
        <span className="status-dot blink" /> {label}
      </p>
      <div className="bar-indeterminate" style={{ marginTop: 16, maxWidth: 360 }} />
    </div>
  );
}

export function Failure({
  error,
  title,
  onRetry,
}: {
  error: Error;
  title: string;
  onRetry?: () => void;
}) {
  return (
    <div className="in-wrap in-section" role="alert">
      <p className="label label-signal">Fault</p>
      <h1 className="display-2" style={{ marginTop: 12 }}>
        {title}
      </h1>
      <p className="alert" style={{ marginTop: 24, maxWidth: 640 }}>
        {error.message}
      </p>
      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        {onRetry && (
          <button type="button" className="btn" onClick={onRetry}>
            Retry <span className="btn-arrow">↻</span>
          </button>
        )}
        <Link to={to.home} className="btn btn-ghost">
          New measurement <span className="btn-arrow">→</span>
        </Link>
      </div>
    </div>
  );
}

export function Logo() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <circle cx="11" cy="11" r="10" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M11 0v5M11 17v5M0 11h5M17 11h5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="11" cy="11" r="2.4" fill="var(--signal)" />
    </svg>
  );
}
